import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Cookie, Shield, BarChart3, Megaphone, SlidersHorizontal, X, Check } from "lucide-react";
import { loadAnalytics } from "../utils/thirdParty";

const STORAGE_KEY = "votra_cookie_consent";
const CONSENT_VERSION = 1;
const EXPIRY_DAYS = 180; // Datatilsynet anbefaler fornyet samtykke ~hver 6.-12. mdr.

const DEFAULT = { necessary: true, functional: false, statistics: false, marketing: false };

const CATEGORIES = [
  { key: "necessary", icon: Shield, title: "Nødvendige", locked: true,
    desc: "Kræves for at hjemmesiden fungerer (fx login, sikkerhed og dine cookie-valg). Kan ikke fravælges." },
  { key: "functional", icon: SlidersHorizontal, title: "Funktionelle",
    desc: "Husker dine indstillinger og valg, så siden fungerer bedre for dig." },
  { key: "statistics", icon: BarChart3, title: "Statistik",
    desc: "Hjælper os med at forstå, hvordan siden bruges, så vi kan forbedre den (anonymiseret)." },
  { key: "marketing", icon: Megaphone, title: "Marketing",
    desc: "Bruges til at vise relevante annoncer og måle effekten af vores markedsføring." },
];

export const getCookieConsent = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
    if (!raw || raw.v !== CONSENT_VERSION) return null;
    const ageDays = (Date.now() - new Date(raw.date).getTime()) / 86400000;
    if (ageDays > EXPIRY_DAYS) return null;
    return raw;
  } catch { return null; }
};

/** Oversætter brugerens valg til Google Consent Mode v2 og til PostHog.
 *  Begge indlæses med samtykke nægtet i index.html, så intet opsamles før dette kald.
 *  Selve scriptene hentes først her (utils/thirdParty.js), aldrig i <head>: de gjorde
 *  en kold crawl af siderne 3 til 5 sekunder lang, fordi prerenderingen venter på netværket. */
export const applyConsent = (prefs) => {
  const granted = (on) => (on ? "granted" : "denied");
  if (typeof window.gtag === "function") {
    window.gtag("consent", "update", {
      ad_storage: granted(prefs.marketing),
      ad_user_data: granted(prefs.marketing),
      ad_personalization: granted(prefs.marketing),
      analytics_storage: granted(prefs.statistics),
      functionality_storage: granted(prefs.functional),
      personalization_storage: granted(prefs.functional),
      security_storage: "granted",
    });
  }
  if (prefs.statistics) {
    loadAnalytics();
  }
  const ph = window.posthog;
  if (ph && typeof ph.opt_in_capturing === "function") {
    if (prefs.statistics) {
      ph.opt_in_capturing();
      if (typeof ph.startSessionRecording === "function") ph.startSessionRecording();
    } else {
      if (typeof ph.stopSessionRecording === "function") ph.stopSessionRecording();
      ph.opt_out_capturing();
    }
  }
};

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [prefs, setPrefs] = useState(DEFAULT);

  useEffect(() => {
    const existing = getCookieConsent();
    if (existing) {
      setPrefs({ ...DEFAULT, ...existing });
      applyConsent({ ...DEFAULT, ...existing });
    } else {
      setVisible(true);
    }
  }, []);

  const openSettings = useCallback(() => {
    const existing = getCookieConsent();
    setPrefs(existing ? { ...DEFAULT, ...existing } : DEFAULT);
    setShowSettings(true);
    setVisible(true);
  }, []);

  useEffect(() => {
    window.addEventListener("open-cookie-settings", openSettings);
    return () => window.removeEventListener("open-cookie-settings", openSettings);
  }, [openSettings]);

  const persist = (choice) => {
    const payload = { ...DEFAULT, ...choice, necessary: true, v: CONSENT_VERSION, date: new Date().toISOString() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    applyConsent(payload);
    window.dispatchEvent(new CustomEvent("votra-cookie-consent-updated", { detail: payload }));
    setPrefs(payload);
    setVisible(false);
    setShowSettings(false);
  };

  const acceptAll = () => persist({ functional: true, statistics: true, marketing: true });
  const rejectAll = () => persist({ functional: false, statistics: false, marketing: false });
  const saveSelection = () => persist(prefs);

  const hasConsent = !!getCookieConsent();

  return (
    <>
      {/* Reopen button (efter valg er truffet) */}
      {hasConsent && !visible && (
        <button
          onClick={openSettings}
          aria-label="Cookieindstillinger"
          className="fixed bottom-4 left-4 z-[60] w-11 h-11 rounded-full bg-white shadow-lg border border-slate-200 flex items-center justify-center text-slate-600 hover:text-[hsl(210,100%,45%)] hover:border-[hsl(210,100%,45%)] hover:scale-105 transition-all duration-200"
          data-testid="cookie-reopen-btn"
        >
          <Cookie className="h-5 w-5" />
        </button>
      )}

      {visible && (
        <div className={`fixed inset-0 z-[70] flex items-end sm:items-center sm:justify-center ${showSettings ? "" : "pointer-events-none"}`}>
          {/* Overlay kun ved detaljeindstillinger, så banneret ikke blokerer men indstillinger gør */}
          <div
            className={`absolute inset-0 bg-slate-900/40 backdrop-blur-[2px] transition-opacity ${showSettings ? "opacity-100" : "opacity-0 pointer-events-none"}`}
            onClick={() => hasConsent && setShowSettings(false)}
            aria-hidden
          />

          {!showSettings ? (
            /* ---------- BANNER ---------- */
            <div
              className="pointer-events-auto relative w-full sm:max-w-2xl sm:mb-6 sm:mx-4 bg-white border border-slate-200 shadow-2xl sm:rounded-2xl p-5 sm:p-6 animate-[slideUp_.35s_ease]"
              role="dialog" aria-live="polite" aria-label="Cookie-samtykke"
              data-testid="cookie-banner"
            >
              <div className="flex items-start gap-3.5">
                <div className="hidden sm:flex w-11 h-11 rounded-xl bg-[hsl(210,100%,45%)]/10 items-center justify-center flex-shrink-0">
                  <Cookie className="h-6 w-6 text-[hsl(210,100%,45%)]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-base font-bold text-slate-900 mb-1">Vi bruger cookies</p>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    Vi bruger nødvendige cookies for at siden fungerer. Med dit samtykke bruger vi også cookies til
                    funktionalitet, statistik og marketing. Du kan til enhver tid ændre eller trække dit samtykke tilbage.
                    Læs mere i vores{" "}
                    <Link to="/persondata" className="text-[hsl(210,100%,45%)] font-medium underline hover:no-underline">
                      persondata- og cookiepolitik
                    </Link>.
                  </p>
                  <div className="mt-4 flex flex-col sm:flex-row gap-2.5">
                    <button
                      onClick={rejectAll}
                      className="flex-1 px-4 py-2.5 rounded-full text-sm font-semibold border border-slate-300 text-slate-700 bg-white hover:bg-slate-50 transition-colors"
                      data-testid="cookie-reject-all"
                    >
                      Afvis alle
                    </button>
                    <button
                      onClick={() => setShowSettings(true)}
                      className="flex-1 px-4 py-2.5 rounded-full text-sm font-semibold border border-slate-300 text-slate-700 bg-white hover:bg-slate-50 transition-colors"
                      data-testid="cookie-customize"
                    >
                      Tilpas
                    </button>
                    <button
                      onClick={acceptAll}
                      className="flex-1 px-4 py-2.5 rounded-full text-sm font-semibold text-white bg-[hsl(210,100%,45%)] hover:bg-[hsl(210,100%,40%)] transition-colors shadow-sm"
                      data-testid="cookie-accept-all"
                    >
                      Accepter alle
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* ---------- INDSTILLINGER ---------- */
            <div
              className="relative w-full sm:max-w-lg sm:mx-4 bg-white sm:rounded-2xl shadow-2xl max-h-[90vh] flex flex-col animate-[slideUp_.35s_ease]"
              role="dialog" aria-modal="true" aria-label="Cookieindstillinger"
              data-testid="cookie-settings"
            >
              <div className="flex items-center justify-between px-5 sm:px-6 py-4 border-b border-slate-200">
                <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <Cookie className="h-5 w-5 text-[hsl(210,100%,45%)]" /> Cookieindstillinger
                </h2>
                {hasConsent && (
                  <button onClick={() => setShowSettings(false)} className="text-slate-400 hover:text-slate-700" aria-label="Luk" data-testid="cookie-settings-close">
                    <X className="h-5 w-5" />
                  </button>
                )}
              </div>

              <div className="px-5 sm:px-6 py-4 overflow-y-auto space-y-3">
                {CATEGORIES.map((cat) => {
                  const Icon = cat.icon;
                  const checked = cat.locked ? true : !!prefs[cat.key];
                  return (
                    <div key={cat.key} className="flex items-start gap-3 p-3.5 rounded-xl border border-slate-200 bg-slate-50/60">
                      <div className="w-9 h-9 rounded-lg bg-white border border-slate-200 flex items-center justify-center flex-shrink-0">
                        <Icon className="h-4.5 w-4.5 text-[hsl(210,100%,45%)]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-semibold text-slate-900">{cat.title}</span>
                          {cat.locked ? (
                            <span className="text-[11px] font-medium text-slate-500 bg-slate-200 px-2 py-0.5 rounded-full">Altid aktiv</span>
                          ) : (
                            <button
                              role="switch" aria-checked={checked}
                              onClick={() => setPrefs((p) => ({ ...p, [cat.key]: !p[cat.key] }))}
                              className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${checked ? "bg-[hsl(210,100%,45%)]" : "bg-slate-300"}`}
                              data-testid={`cookie-toggle-${cat.key}`}
                            >
                              <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${checked ? "translate-x-5" : ""}`} />
                            </button>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 leading-relaxed mt-1">{cat.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="px-5 sm:px-6 py-4 border-t border-slate-200 flex flex-col sm:flex-row gap-2.5">
                <button onClick={rejectAll} className="flex-1 px-4 py-2.5 rounded-full text-sm font-semibold border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors" data-testid="cookie-settings-reject">
                  Afvis alle
                </button>
                <button onClick={saveSelection} className="flex-1 px-4 py-2.5 rounded-full text-sm font-semibold border border-[hsl(210,100%,45%)] text-[hsl(210,100%,45%)] hover:bg-[hsl(210,100%,45%)]/5 transition-colors" data-testid="cookie-save-selection">
                  Gem mine valg
                </button>
                <button onClick={acceptAll} className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full text-sm font-semibold text-white bg-[hsl(210,100%,45%)] hover:bg-[hsl(210,100%,40%)] transition-colors" data-testid="cookie-settings-accept">
                  <Check className="h-4 w-4" /> Accepter alle
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default CookieConsent;
